import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { IconSend, IconBug, IconRefresh } from '@tabler/icons-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
}

export function TestPanel() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Olá! Como posso ajudar você hoje?',
      timestamp: new Date(),
    },
  ])
  const [input, setInput] = useState('')
  const [debugDialogOpen, setDebugDialogOpen] = useState(false)
  const [, setSelectedMessageDebug] = useState<string | null>(null)

  const sendMessage = () => {
    if (!input.trim()) return

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput('')

    // Simular resposta do agente
    setTimeout(() => {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: getMockResponse(input),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, assistantMessage])
    }, 1000)
  }

  const getMockResponse = (userInput: string): string => {
    const lower = userInput.toLowerCase()
    if (lower.includes('olá') || lower.includes('oi')) {
      return 'Olá! É um prazer conversar com você. Como posso ser útil hoje?'
    }
    if (lower.includes('horário') || lower.includes('funciona')) {
      return 'Nosso horário de funcionamento é de segunda a sexta, das 9h às 18h.'
    }
    if (lower.includes('preço') || lower.includes('custo')) {
      return 'Temos diversos planos que se adaptam às suas necessidades. Gostaria que eu enviasse mais informações sobre nossos preços?'
    }
    return 'Entendo. Posso ajudar com mais alguma coisa?'
  }

  const resetChat = () => {
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: 'Olá! Como posso ajudar você hoje?',
        timestamp: new Date(),
      },
    ])
  }

  const openDebug = (messageId: string) => {
    setSelectedMessageDebug(messageId)
    setDebugDialogOpen(true)
  }

  const mockDebugInfo = {
    stage: 'initial_greeting',
    tokensUsed: 142,
    model: 'gpt-4o-mini',
    processingTime: '1.2s',
    reasoning: [
      '1. Detectou saudação do usuário',
      '2. Aplicou tom de voz: Prático',
      '3. Utilizou mensagem inicial configurada',
      '4. Nenhum estágio específico ativado',
    ],
  }

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='pb-3'>
        <div className='flex items-center justify-between'>
          <CardTitle className='text-base'>Painel de Teste</CardTitle>
          <Button variant='ghost' size='icon' onClick={resetChat}>
            <IconRefresh className='w-4 h-4' />
          </Button>
        </div>
        <p className='text-xs text-muted-foreground'>
          Teste seu agente sem gastar créditos
        </p>
      </CardHeader>

      <Separator />

      <CardContent className='flex-1 p-0 flex flex-col'>
        {/* Messages Area */}
        <ScrollArea className='flex-1 p-4'>
          <div className='space-y-4'>
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  'flex',
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                )}
              >
                <div
                  className={cn(
                    'max-w-[80%] rounded-lg p-3 space-y-1',
                    message.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  )}
                >
                  <p className='text-sm'>{message.content}</p>
                  <div className='flex items-center justify-between gap-2'>
                    <span className='text-xs opacity-70'>
                      {message.timestamp.toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                    {message.role === 'assistant' && (
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-5 px-2 text-xs'
                        onClick={() => openDebug(message.id)}
                      >
                        <IconBug className='w-3 h-3 mr-1' />
                        Debug
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        <Separator />

        {/* Input Area */}
        <div className='p-4 space-y-2'>
          <div className='flex gap-2'>
            <Input
              placeholder='Digite sua mensagem...'
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            />
            <Button onClick={sendMessage} size='icon'>
              <IconSend className='w-4 h-4' />
            </Button>
          </div>
          <p className='text-xs text-muted-foreground'>
            Este é um ambiente de teste. Nenhum crédito será consumido.
          </p>
        </div>
      </CardContent>

      {/* Debug Dialog */}
      <Dialog open={debugDialogOpen} onOpenChange={setDebugDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inspecionar Resposta</DialogTitle>
            <DialogDescription>
              Veja o raciocínio da IA e detalhes da resposta gerada.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-1'>
                <p className='text-xs text-muted-foreground'>Modelo</p>
                <p className='text-sm font-medium'>{mockDebugInfo.model}</p>
              </div>
              <div className='space-y-1'>
                <p className='text-xs text-muted-foreground'>Tempo</p>
                <p className='text-sm font-medium'>
                  {mockDebugInfo.processingTime}
                </p>
              </div>
              <div className='space-y-1'>
                <p className='text-xs text-muted-foreground'>Tokens</p>
                <p className='text-sm font-medium'>{mockDebugInfo.tokensUsed}</p>
              </div>
              <div className='space-y-1'>
                <p className='text-xs text-muted-foreground'>Estágio</p>
                <p className='text-sm font-medium'>{mockDebugInfo.stage}</p>
              </div>
            </div>

            <Separator />

            <div className='space-y-2'>
              <p className='text-sm font-medium'>Raciocínio da IA:</p>
              <div className='p-3 bg-muted rounded-lg space-y-2'>
                {mockDebugInfo.reasoning.map((step, index) => (
                  <p key={index} className='text-sm'>
                    {step}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
